import { Check, KeyRound, LogOut, MapPin, Menu, User2 } from 'lucide-react';
import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LayoutHook } from 'slices/layouts/hook';

//import images
import logoDark from 'assets/images/logo-dark.png';
import logoLight from 'assets/images/logo-light.png';
import logoSm from 'assets/images/logo-sm.png';
// import userProfile from 'assets/images/users/user-profile.png';

import ChangePasswordModal from 'Common/Components/ChangePasswordModal';
import { Dropdown } from 'Common/Components/Dropdown';
import { IOptions } from 'Common/Components/Select';
import useIsMobile from 'Common/useIsMobile';
import { decodeJwt, joinName } from 'helpers/utils';
import i18n from 'i18n';
import { appDispatch } from 'slices';
import { LoginHook } from 'slices/auth/login/hook';
import { PublicHook } from 'slices/public/hook';
import { getPublicTenantsByChain } from 'slices/public/thunk';
import { changeLayoutSidebarVisible, changeLeftsidebarSizeType, switchTenant } from 'slices/thunk';
import { logoutUser } from 'slices/auth/login/reducer';

const Header = () => {
  const dispatch = appDispatch();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { tenants } = PublicHook();
  const { account } = LoginHook();

  const [selectedBase, setSelectedBase] = React.useState<IOptions | null>(null);
  const [tenantIdFromToken, setTenantIdFromToken] = React.useState<number | null>(null);
  const [openChangePasswordModal, setOpenChangePasswordModal] = React.useState(false);

  const tenantOptions: IOptions[] = React.useMemo(
    () =>
      (tenants || [])
        .filter((t: any) => t.isActive)
        .map((t: any) => ({
          value: t.id,
          label: t.name,
          subLabel: t.address,
        })),
    [tenants],
  );

  useEffect(() => {
    const subdomain = window.location.hostname.split('.')[0];
    dispatch(getPublicTenantsByChain(subdomain));
  }, []);

  useEffect(() => {
    if (!tenants || tenants.length === 0) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    const decoded = decodeJwt(token);
    if (decoded?.tenant_id) {
      setTenantIdFromToken(decoded?.tenant_id);
    }
  }, [tenants]);

  useEffect(() => {
    if (!tenantOptions || tenantOptions.length === 0) return;
    const found = tenantOptions.find((o) => o.value === tenantIdFromToken);
    setSelectedBase(found ?? tenantOptions[0]);
  }, [tenantOptions, tenantIdFromToken]);

  const handleChangeTenant = async (opt: IOptions) => {
    if (!opt || opt.value === selectedBase?.value) return;
    setSelectedBase(opt);
    try {
      const response: any = await dispatch(switchTenant({ tenantId: opt.value })).unwrap();
      if (response?.id_token) {
        localStorage.setItem('token', response.id_token);
        window.location.reload();
        return;
      }
      localStorage.removeItem('token');
      navigate('/login');
    } catch (error) {
      localStorage.removeItem('token');
      navigate('/login');
    }
  };

  const { layoutSidebarSizeType, layoutType, layoutSidebarVisible } = LayoutHook();

  const handleTopbarHamburgerIcon = () => {
    const windowSize = document.documentElement.clientWidth;

    if (windowSize < 768) {
      // toggle sidebar visibility via Redux state; Sidebar component will handle body scroll
      dispatch(changeLayoutSidebarVisible(!layoutSidebarVisible));
      dispatch(changeLeftsidebarSizeType('lg'));
    } else {
      dispatch(changeLeftsidebarSizeType(layoutSidebarSizeType === 'sm' ? 'lg' : 'sm'));
    }
  };

  useEffect(() => {
    i18n.changeLanguage('vi');
    localStorage.setItem('I18N_LANGUAGE', 'vi');
  }, []);

  useEffect(() => {
    // resize
    const handleResizeLayout = () => {
      var windowSize = document.documentElement.clientWidth;

      if (windowSize < 768) {
        dispatch(changeLeftsidebarSizeType('lg'));
      } else if (windowSize <= 1024) {
        if (layoutType === 'vertical') {
          dispatch(changeLeftsidebarSizeType('sm'));
        } else {
          dispatch(changeLeftsidebarSizeType('lg'));
        }
      } else {
        dispatch(changeLeftsidebarSizeType('lg'));

        // dispatch(changeLeftsidebarSizeType(layoutSidebarSizeType === "sm" ? "lg" : "sm"));
      }
    };

    // out side click
    const outerSideElement = (e: any) => {
      var windowSize = document.documentElement.clientWidth;
      // if clicked overlay, close sidebar on mobile; otherwise on larger screens reset size
      if (e?.target?.id === 'sidebar-overlay') {
        if (windowSize <= 768) {
          dispatch(changeLayoutSidebarVisible(false));
        } else {
          dispatch(changeLeftsidebarSizeType('lg'));
        }
      }
    };

    // scroll
    const scrollNavigation = () => {
      var scrollUp = document.documentElement.scrollTop;
      if (scrollUp >= 50) {
        document.getElementById('page-topbar')?.classList.add('is-sticky');
      } else {
        document.getElementById('page-topbar')?.classList.remove('is-sticky');
      }
    };

    window.addEventListener('scroll', scrollNavigation, true);
    window.addEventListener('click', outerSideElement);
    window.addEventListener('resize', handleResizeLayout);

    // Cleanup function to remove the event listeners
    return () => {
      window.removeEventListener('scroll', scrollNavigation, true);
      window.removeEventListener('click', outerSideElement);
      window.removeEventListener('resize', handleResizeLayout);
    };
  }, [layoutType]);

  return (
    <React.Fragment>
      <ChangePasswordModal
        open={openChangePasswordModal}
        onClose={() => setOpenChangePasswordModal(false)}
      />
      <header
        id="page-topbar"
        className="ltr:md:left-vertical-menu rtl:md:right-vertical-menu group-data-[sidebar-size=md]:ltr:md:left-vertical-menu-md group-data-[sidebar-size=md]:rtl:md:right-vertical-menu-md group-data-[sidebar-size=sm]:ltr:md:left-vertical-menu-sm group-data-[sidebar-size=sm]:rtl:md:right-vertical-menu-sm group-data-[layout=horizontal]:ltr:left-0 group-data-[layout=horizontal]:rtl:right-0 fixed right-0 z-[1000] left-0 print:hidden group-data-[navbar=bordered]:m-4 group-data-[navbar=bordered]:[&.is-sticky]:mt-0 transition-all ease-linear duration-300 group-data-[navbar=hidden]:hidden group-data-[navbar=scroll]:absolute group/topbar group-data-[layout=horizontal]:z-[1004]"
      >
        <div className="layout-width">
          <div className="flex items-center px-4 mx-auto bg-topbar border-b-2 border-topbar group-data-[topbar=dark]:bg-topbar-dark group-data-[topbar=dark]:border-topbar-dark group-data-[topbar=brand]:bg-topbar-brand group-data-[topbar=brand]:border-topbar-brand shadow-md h-header shadow-slate-200/50 group-data-[navbar=bordered]:rounded-md group-data-[navbar=bordered]:group-[.is-sticky]/topbar:rounded-t-none group-data-[topbar=dark]:dark:bg-zink-700 group-data-[topbar=dark]:dark:border-zink-700 dark:shadow-none group-data-[topbar=dark]:group-[.is-sticky]/topbar:dark:shadow-zink-500 group-data-[topbar=dark]:group-[.is-sticky]/topbar:dark:shadow-md group-data-[navbar=bordered]:shadow-none group-data-[layout=horizontal]:group-data-[navbar=bordered]:rounded-b-none group-data-[layout=horizontal]:shadow-none group-data-[layout=horizontal]:dark:group-[.is-sticky]/topbar:shadow-none">
            <div className="relative flex items-center w-full group-data-[layout=horizontal]:mx-auto group-data-[layout=horizontal]:max-w-screen-2xl navbar-header group-data-[layout=horizontal]:ltr:xl:pr-3 group-data-[layout=horizontal]:rtl:xl:pl-3">
              <div className="items-center justify-center hidden px-5 text-center h-header group-data-[layout=horizontal]:md:flex group-data-[layout=horizontal]:ltr::pl-0 group-data-[layout=horizontal]:rtl:pr-0">
                <Link to="/">
                  <span className="hidden">
                    <img src={logoSm} alt="" className="h-6 mx-auto" />
                  </span>
                  <span className="group-data-[topbar=dark]:hidden group-data-[topbar=brand]:hidden">
                    <img src={logoDark} alt="" className="h-6 mx-auto" />
                  </span>
                </Link>
                <Link
                  to="/"
                  className="hidden group-data-[topbar=dark]:block group-data-[topbar=brand]:block"
                >
                  <span className="group-data-[topbar=dark]:hidden group-data-[topbar=brand]:hidden">
                    <img src={logoSm} alt="" className="h-6 mx-auto" />
                  </span>
                  <span className="group-data-[topbar=dark]:block group-data-[topbar=brand]:block">
                    <img src={logoLight} alt="" className="h-6 mx-auto" />
                  </span>
                </Link>
              </div>

              {/* <button
                onClick={handleTopbarHamburgerIcon}
                type="button"
                className="inline-flex relative justify-center items-center p-0 text-topbar-item transition-all size-[37.5px] duration-75 ease-linear bg-topbar rounded-md btn hover:bg-slate-100 group-data-[topbar=dark]:bg-topbar-dark group-data-[topbar=dark]:border-topbar-dark group-data-[topbar=dark]:text-topbar-item-dark group-data-[topbar=dark]:hover:bg-topbar-item-bg-hover-dark group-data-[topbar=dark]:hover:text-topbar-item-hover-dark group-data-[topbar=brand]:bg-topbar-brand group-data-[topbar=brand]:border-topbar-brand group-data-[topbar=brand]:text-topbar-item-brand group-data-[topbar=brand]:hover:bg-topbar-item-bg-hover-brand group-data-[topbar=brand]:hover:text-topbar-item-hover-brand group-data-[topbar=dark]:dark:bg-zink-700 group-data-[topbar=dark]:dark:text-zink-200 group-data-[topbar=dark]:dark:border-zink-700 group-data-[topbar=dark]:dark:hover:bg-zink-600 group-data-[topbar=dark]:dark:hover:text-zink-50 group-data-[layout=horizontal]:flex group-data-[layout=horizontal]:md:hidden hamburger-icon"
                id="topnav-hamburger-icon"
              >
                <ChevronsLeft className="w-5 h-5 group-data-[sidebar-size=sm]:hidden" />
                <ChevronsRight className="hidden w-5 h-5 group-data-[sidebar-size=sm]:block" />
              </button> */}

              {isMobile && (
                <button
                  onClick={handleTopbarHamburgerIcon}
                  type="button"
                  className="inline-flex relative justify-center items-center p-0 text-topbar-item transition-all size-[37.5px] duration-75 ease-linear bg-topbar rounded-md btn hover:bg-slate-100 group-data-[topbar=dark]:bg-topbar-dark group-data-[topbar=dark]:border-topbar-dark group-data-[topbar=dark]:text-topbar-item-dark group-data-[topbar=dark]:hover:bg-topbar-item-bg-hover-dark group-data-[topbar=dark]:hover:text-topbar-item-hover-dark group-data-[topbar=brand]:bg-topbar-brand group-data-[topbar=brand]:border-topbar-brand group-data-[topbar=brand]:text-topbar-item-brand group-data-[topbar=brand]:hover:bg-topbar-item-bg-hover-brand group-data-[topbar=brand]:hover:text-topbar-item-hover-brand group-data-[topbar=dark]:dark:bg-zink-700 group-data-[topbar=dark]:dark:text-zink-200 group-data-[topbar=dark]:dark:border-zink-700 group-data-[topbar=dark]:dark:hover:bg-zink-600 group-data-[topbar=dark]:dark:hover:text-zink-50 group-data-[layout=horizontal]:flex group-data-[layout=horizontal]:md:hidden hamburger-icon"
                  id="topnav-hamburger-icon"
                >
                  <Menu className="w-5 h-5 group-data-[sidebar-size=sm]:hidden" />
                </button>
              )}

              {/* {isMobile && (
                <div className="absolute left-1/2 -translate-x-1/2 pointer-events-none z-10">
                  <img src="/logo.svg" alt="logo" className="h-7 w-auto" />
                </div>
              )} */}

              {/* <input
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="Nhập số"
                className="w-full border border-gray-300 rounded-md pl-8 pr-3 py-2 text-[16px] sm:text-16 focus:outline-none focus:ring-2 focus:ring-blue-500"
              /> */}

              {/* <div className="relative hidden ltr:ml-3 rtl:mr-3 lg:block group-data-[layout=horizontal]:hidden group-data-[layout=horizontal]:lg:block">
                <input
                  type="text"
                  className="py-2 pr-4 text-sm text-topbar-item bg-topbar border border-topbar-border rounded pl-8 placeholder:text-slate-400 form-control focus-visible:outline-0 min-w-[300px] focus:border-blue-400 group-data-[topbar=dark]:bg-topbar-dark group-data-[topbar=dark]:border-topbar-border-dark group-data-[topbar=dark]:placeholder:text-slate-500 group-data-[topbar=dark]:text-topbar-item-dark group-data-[topbar=brand]:bg-topbar-brand group-data-[topbar=brand]:border-topbar-border-brand group-data-[topbar=brand]:placeholder:text-blue-300 group-data-[topbar=brand]:text-topbar-item-brand group-data-[topbar=dark]:dark:bg-zink-700 group-data-[topbar=dark]:dark:border-zink-500 group-data-[topbar=dark]:dark:text-zink-100"
                  placeholder="Tìm kiếm"
                  autoComplete="off"
                />
                <Search className="inline-block size-4 absolute left-2.5 top-3 text-topbar-item fill-slate-100 group-data-[topbar=dark]:fill-topbar-item-bg-hover-dark group-data-[topbar=dark]:text-topbar-item-dark group-data-[topbar=brand]:fill-topbar-item-bg-hover-brand group-data-[topbar=brand]:text-topbar-item-brand group-data-[topbar=dark]:dark:text-zink-200 group-data-[topbar=dark]:dark:fill-zink-600" />
              </div> */}

              {/* <div className="relative flex-grow mx-2">
                  <input
                    type="search"
                    value={search}
                    onChange={handleChange}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleSearch();
                        e.currentTarget.blur();
                      }
                    }}
                    placeholder="SĐT, Biển số xe, Tên khách hàng, ..."
                    className="w-full bg-white border border-gray-300 rounded-md pl-8 pr-3 py-2 text-[16px] sm:text-16 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <Search className="inline-block size-4 absolute ltr:left-2.5 rtl:right-2.5 top-3 text-slate-500 dark:text-zink-200 fill-slate-100 dark:fill-zink-600" />
                </div> */}

              <div className="flex gap-3 w-full">
                <div className="flex-grow flex items-center justify-center text-blue-default text-lg font-semibold">
                  {selectedBase?.label}
                </div>
                {/* LanguageDropdown */}
                {/* <LanguageDropdown /> */}

                {/* LightDark */}
                {/* <LightDark /> */}

                {/* AddToCartDrawer */}
                {/* <div className="relative flex items-center h-header">
                  <button
                    type="button"
                    data-drawer-target="cartSidePenal"
                    className="inline-flex relative justify-center items-center p-0 text-topbar-item transition-all size-[37.5px] duration-200 ease-linear bg-topbar rounded-md btn hover:bg-topbar-item-bg-hover hover:text-topbar-item-hover group-data-[topbar=dark]:bg-topbar-dark group-data-[topbar=dark]:hover:bg-topbar-item-bg-hover-dark group-data-[topbar=dark]:hover:text-topbar-item-hover-dark group-data-[topbar=brand]:bg-topbar-brand group-data-[topbar=brand]:hover:bg-topbar-item-bg-hover-brand group-data-[topbar=brand]:hover:text-topbar-item-hover-brand group-data-[topbar=dark]:dark:bg-zink-700 group-data-[topbar=dark]:dark:hover:bg-zink-600 group-data-[topbar=brand]:text-topbar-item-brand group-data-[topbar=dark]:dark:hover:text-zink-50 group-data-[topbar=dark]:dark:text-zink-200 group-data-[topbar=dark]:text-topbar-item-dark"
                    onClick={handleDrawer}
                  >
                    <ShoppingCart className="inline-block size-5 stroke-1 fill-slate-100 group-data-[topbar=dark]:fill-topbar-item-bg-hover-dark group-data-[topbar=brand]:fill-topbar-item-bg-hover-brand"></ShoppingCart>
                    <span className="absolute flex items-center justify-center w-[16px] h-[16px] text-xs text-white bg-red-400 border-white rounded-full -top-1 -right-1">
                      3
                    </span>
                  </button>
                </div> */}

                {/* NotificationDropdown */}
                {/* <NotificationDropdown /> */}

                {/* <div className="relative items-center hidden h-header md:flex">
                  <button
                    onClick={handleToggleDrawer}
                    data-drawer-target="customizerButton"
                    type="button"
                    className="inline-flex justify-center items-center p-0 text-topbar-item transition-all size-[37.5px] duration-200 ease-linear bg-topbar group-data-[topbar=dark]:text-topbar-item-dark rounded-md btn hover:bg-topbar-item-bg-hover hover:text-topbar-item-hover group-data-[topbar=dark]:bg-topbar-dark group-data-[topbar=dark]:hover:bg-topbar-item-bg-hover-dark group-data-[topbar=dark]:hover:text-topbar-item-hover-dark group-data-[topbar=brand]:bg-topbar-brand group-data-[topbar=brand]:hover:bg-topbar-item-bg-hover-brand group-data-[topbar=brand]:hover:text-topbar-item-hover-brand group-data-[topbar=dark]:dark:bg-zink-700 group-data-[topbar=dark]:dark:hover:bg-zink-600 group-data-[topbar=brand]:text-topbar-item-brand group-data-[topbar=dark]:dark:hover:text-zink-50 group-data-[topbar=dark]:dark:text-zink-200"
                  >
                    <Settings className="inline-block size-5 stroke-1 fill-slate-100 group-data-[topbar=dark]:fill-topbar-item-bg-hover-dark group-data-[topbar=brand]:fill-topbar-item-bg-hover-brand"></Settings>
                  </button>
                </div> */}

                <Dropdown className="relative flex items-center h-header">
                  <Dropdown.Trigger
                    type="button"
                    className="inline-block p-0 transition-all duration-200 ease-linear bg-topbar rounded-full text-topbar-item dropdown-toggle btn hover:bg-topbar-item-bg-hover hover:text-topbar-item-hover group-data-[topbar=dark]:text-topbar-item-dark group-data-[topbar=dark]:bg-topbar-dark group-data-[topbar=dark]:hover:bg-topbar-item-bg-hover-dark group-data-[topbar=dark]:hover:text-topbar-item-hover-dark group-data-[topbar=brand]:bg-topbar-brand group-data-[topbar=brand]:hover:bg-topbar-item-bg-hover-brand group-data-[topbar=brand]:hover:text-topbar-item-hover-brand group-data-[topbar=dark]:dark:bg-zink-700 group-data-[topbar=dark]:dark:hover:bg-zink-600 group-data-[topbar=brand]:text-topbar-item-brand group-data-[topbar=dark]:dark:hover:text-zink-50 group-data-[topbar=dark]:dark:text-zink-200"
                    id="dropdownMenuButton"
                    data-bs-toggle="dropdown"
                  >
                    <div className="size-10 rounded-full inline-flex items-center justify-center bg-slate-200 dark:bg-zink-500">
                      <User2 className="size-6 " />
                    </div>
                  </Dropdown.Trigger>
                  <Dropdown.Content
                    placement="right-end"
                    className="absolute z-50 p-4 ltr:text-left rtl:text-right bg-white rounded-md shadow-md !top-4 dropdown-menu min-w-[16rem] dark:bg-zink-600"
                    aria-labelledby="dropdownMenuButton"
                  >
                    <h6 className="mb-2 text-sm font-normal text-slate-500 dark:text-zink-300">
                      Welcome!
                    </h6>
                    <a href="#!" className="flex gap-3 mb-3">
                      <div className="relative inline-block shrink-0">
                        <div className="rounded bg-slate-100 dark:bg-zink-500">
                          {/* <img src={userProfile} alt="" className="size-12 rounded" /> */}
                          <User2 className="size-12 rounded" />
                        </div>
                        <span className="-top-1 ltr:-right-1 rtl:-left-1 absolute size-2.5 bg-green-400 border-2 border-white rounded-full dark:border-zink-600"></span>
                      </div>
                      <div>
                        <h6 className="text-15">
                          {joinName([account?.lastName, account?.firstName]) || 'admin'}
                        </h6>
                        <p className="text-slate-500 dark:text-zink-300">{account?.email ?? ''}</p>
                      </div>
                    </a>
                    <ul>
                      <li className="pb-2 mb-2 border-b border-slate-200 dark:border-zink-500">
                        <div className="flex justify-between items-center">
                          <p className="py-1 text-xs font-semibold uppercase tracking-wide text-slate-800 dark:text-zink-300">
                            Cửa hàng
                          </p>
                          <span className="rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-xs font-medium text-sky-700 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-300">
                            {tenantOptions.length} cơ sở
                          </span>
                        </div>
                        <div
                          className={`mt-1 space-y-1 ${tenantOptions.length > 4 ? 'max-h-56 overflow-y-auto' : ''}`}
                        >
                          {tenantOptions.length > 0 ? (
                            tenantOptions.map((option) => {
                              const isActive = selectedBase?.value === option.value;

                              return (
                                <button
                                  key={String(option.value)}
                                  type="button"
                                  className={`flex w-full items-center gap-2 rounded-xl border px-2 py-1 text-left shadow-sm transition-all duration-200 ease-linear ${
                                    isActive
                                      ? 'border-custom-200 bg-white text-custom-600 ring-1 ring-custom-100 dark:border-custom-500/40 dark:bg-zink-600 dark:text-custom-300 dark:ring-custom-500/20'
                                      : 'border-transparent bg-white/80 text-slate-600 hover:border-slate-200 hover:bg-white hover:text-custom-500 dark:bg-zink-600/70 dark:text-zink-200 dark:hover:border-zink-500 dark:hover:bg-zink-600 dark:hover:text-custom-400'
                                  }`}
                                  onClick={() => handleChangeTenant(option)}
                                >
                                  <span
                                    className={`mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg ${
                                      isActive
                                        ? 'bg-custom-100 text-custom-600 dark:bg-custom-500/15 dark:text-custom-300'
                                        : 'bg-slate-100 text-slate-500 dark:bg-zink-500 dark:text-zink-200'
                                    }`}
                                  >
                                    <MapPin className="size-4 shrink-0" />
                                  </span>
                                  <span className="min-w-0 flex-1">
                                    <span className="block text-sm font-medium">
                                      {option.label}
                                    </span>
                                    {option.subLabel ? (
                                      <span className="mt-1 block text-xs text-slate-400 dark:text-zink-300">
                                        {option.subLabel}
                                      </span>
                                    ) : null}
                                  </span>
                                  <span className="flex min-h-4 min-w-4 items-center justify-center">
                                    {isActive ? (
                                      <span className="flex size-4 items-center justify-center rounded-full bg-custom-500 text-white dark:bg-custom-400 dark:text-zink-900">
                                        <Check className="size-3 shrink-0" />
                                      </span>
                                    ) : null}
                                  </span>
                                </button>
                              );
                            })
                          ) : (
                            <p className="px-4 py-2 text-sm text-slate-400 dark:text-zink-300">
                              Không có cơ sở
                            </p>
                          )}
                        </div>
                      </li>
                      <li>
                        <button
                          type="button"
                          className="flex w-full items-center ltr:pr-4 rtl:pl-4 py-1.5 text-base font-medium text-slate-600 transition-all duration-200 ease-linear dropdown-item hover:text-custom-500 focus:text-custom-500 dark:text-zink-200 dark:hover:text-custom-500 dark:focus:text-custom-500"
                          onClick={() => setOpenChangePasswordModal(true)}
                        >
                          <KeyRound className="inline-block size-4 ltr:mr-2 rtl:ml-2" /> Đổi mật
                          khẩu
                        </button>
                      </li>
                      {/* <li>
                        <a
                          className="block ltr:pr-4 rtl:pl-4 py-1.5 text-base font-medium transition-all duration-200 ease-linear text-slate-600 dropdown-item hover:text-custom-500 focus:text-custom-500 dark:text-zink-200 dark:hover:text-custom-500 dark:focus:text-custom-500"
                          href={process.env.PUBLIC_URL + '/user-profile'}
                        >
                          <User2 className="inline-block size-4 ltr:mr-2 rtl:ml-2"></User2> Profile
                        </a>
                      </li>
                      <li>
                        <a
                          className="block ltr:pr-4 rtl:pl-4 py-1.5 text-base font-medium transition-all duration-200 ease-linear text-slate-600 dropdown-item hover:text-custom-500 focus:text-custom-500 dark:text-zink-200 dark:hover:text-custom-500 dark:focus:text-custom-500"
                          href={process.env.PUBLIC_URL + '/apps-mailbox'}
                        >
                          <Mail className="inline-block size-4 ltr:mr-2 rtl:ml-2"></Mail> Inbox{' '}
                          <span className="inline-flex items-center justify-center size-5 ltr:ml-2 rtl:mr-2 text-[11px] font-medium border rounded-full text-white bg-red-500 border-red-500">
                            15
                          </span>
                        </a>
                      </li>
                      <li>
                        <a
                          className="block ltr:pr-4 rtl:pl-4 py-1.5 text-base font-medium transition-all duration-200 ease-linear text-slate-600 dropdown-item hover:text-custom-500 focus:text-custom-500 dark:text-zink-200 dark:hover:text-custom-500 dark:focus:text-custom-500"
                          href={process.env.PUBLIC_URL + '/apps-chat'}
                        >
                          <MessagesSquare className="inline-block size-4 ltr:mr-2 rtl:ml-2"></MessagesSquare>{' '}
                          Chat
                        </a>
                      </li>
                      <li>
                        <a
                          className="block ltr:pr-4 rtl:pl-4 py-1.5 text-base font-medium transition-all duration-200 ease-linear text-slate-600 dropdown-item hover:text-custom-500 focus:text-custom-500 dark:text-zink-200 dark:hover:text-custom-500 dark:focus:text-custom-500"
                          href={process.env.PUBLIC_URL + '/pages-pricing'}
                        >
                          <Gem className="inline-block size-4 ltr:mr-2 rtl:ml-2"></Gem> Upgrade{' '}
                          <span className="inline-flex items-center justify-center w-auto h-5 ltr:ml-2 rtl:mr-2 px-1 text-[12px] font-medium border rounded text-white bg-sky-500 border-sky-500">
                            Pro
                          </span>
                        </a>
                      </li> */}
                      <li className="pt-2 mt-2 border-t border-slate-200 dark:border-zink-500">
                        <a
                          className="block ltr:pr-4 rtl:pl-4 py-1.5 text-base font-medium transition-all duration-200 ease-linear text-slate-600 dropdown-item hover:text-custom-500 focus:text-custom-500 dark:text-zink-200 dark:hover:text-custom-500 dark:focus:text-custom-500"
                          onClick={() => {
                            dispatch(logoutUser());
                            localStorage.removeItem('token');
                          }}
                          href={process.env.PUBLIC_URL + '/login'}
                        >
                          <LogOut className="inline-block size-4 ltr:mr-2 rtl:ml-2"></LogOut> Đăng xuất
                        </a>
                      </li>
                    </ul>
                  </Dropdown.Content>
                </Dropdown>
              </div>
            </div>
          </div>
        </div>
      </header>
    </React.Fragment>
  );
};

export default Header;
